/**
 * SereneZoneLayout Component
 *
 * Route-level layout for the Serene Zone breathing exercises.
 * Provides a persistent full-screen immersive experience that eliminates
 * flash during navigation between breathing exercises.
 *
 * Key Features:
 * - Persistent background that never unmounts during navigation
 * - Shared controls (theme toggle, exit button, audio player)
 * - Child routes render via <Outlet />
 * - Full viewport with no standard app chrome
 * - Accessibility: Escape key exit, focus management, reduced motion support
 *
 * Architecture:
 * This layout sits OUTSIDE PublicLayout in the router, as a sibling route.
 * This ensures the serene experience is completely separate from the main
 * site's header/footer, similar to OnboardingRouteLayout.
 *
 * @see docs/taskmaster/reports/holding/SERENE_BREATHING_DESIGN_SPEC.md
 */

'use client';

import { motion, useReducedMotion } from 'framer-motion';
import { X, Settings } from 'lucide-react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useCallback, useEffect, useRef, useState } from 'react';
import { cn } from '@/utils/cn';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { AnimatedThemeToggler } from '@/components/ui/animated-theme-toggler';
import { SereneBackground } from './serene-background';
import { SereneAudioPlayer } from './serene-audio-player';
import { QuickSettingsPanel } from './quick-settings-panel';
import { SereneProvider, useSerenePreferences } from '@/providers/serene-provider';
import { ROUTES } from '@/config/routes';

// =============================================================================
// ANIMATION VARIANTS
// =============================================================================

/**
 * Container animation variants for initial entry
 * NOTE: Removed blur filter animation - CSS filters are extremely expensive
 * and cause continuous 120fps repaints, consuming significant CPU.
 */
const containerVariants = {
  initial: {
    opacity: 0,
    scale: 0.98,
  },
  animate: {
    opacity: 1,
    scale: 1,
    transition: {
      duration: 0.4,
      ease: [0.22, 1, 0.36, 1],
    },
  },
};

/**
 * Reduced motion variants - no scale/blur animation
 */
const reducedMotionVariants = {
  initial: { opacity: 0 },
  animate: {
    opacity: 1,
    transition: { duration: 0.3 },
  },
};

/**
 * Controls animation variants (theme toggle, exit button)
 */
const controlsVariants = {
  initial: { opacity: 0, y: -10 },
  animate: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.3,
      delay: 0.4,
    },
  },
};

/**
 * Reduced motion controls variants
 */
const reducedMotionControlsVariants = {
  initial: { opacity: 0 },
  animate: {
    opacity: 1,
    transition: { duration: 0.3 },
  },
};

// =============================================================================
// INNER LAYOUT (requires SereneProvider context)
// =============================================================================

/**
 * Inner layout component that has access to SereneProvider context
 */
function SereneZoneLayoutInner() {
  const navigate = useNavigate();
  const location = useLocation();
  const shouldReduceMotion = useReducedMotion();
  const { preferences, updatePreference } = useSerenePreferences();
  const [showSettings, setShowSettings] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const exitButtonRef = useRef<HTMLButtonElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  /**
   * Determine exit destination based on current location:
   * - From breathing index → resource library
   * - From individual exercises → breathing index
   */
  const getExitDestination = useCallback(() => {
    const isBreathingIndex = location.pathname === ROUTES.PUBLIC_TOOLS_BREATHING;
    return isBreathingIndex ? ROUTES.PUBLIC_RESOURCE_LIBRARY : ROUTES.PUBLIC_TOOLS_BREATHING;
  }, [location.pathname]);

  /**
   * Get tooltip text for exit button based on destination
   */
  const exitTooltipText = location.pathname === ROUTES.PUBLIC_TOOLS_BREATHING
    ? 'Back to Resource Library'
    : 'Back to technique selection';

  /**
   * Handle exit navigation
   */
  const handleExit = useCallback(() => {
    navigate(getExitDestination());
  }, [navigate, getExitDestination]);

  /**
   * Keyboard event handler - Escape key exits serene zone
   */
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        handleExit();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleExit]);

  /**
   * Focus management - store previous focus for restoration on unmount
   * NOTE: We don't auto-focus the exit button to avoid showing focus ring on page load/refresh.
   * Users can tab to it, and keyboard users can press Escape to exit.
   */
  useEffect(() => {
    // Store the previously focused element for restoration when exiting
    previousFocusRef.current = document.activeElement as HTMLElement;

    // Restore focus on unmount (when navigating away)
    return () => {
      setTimeout(() => {
        if (
          previousFocusRef.current &&
          document.body.contains(previousFocusRef.current)
        ) {
          previousFocusRef.current.focus();
        }
      }, 0);
    };
  }, []);

  /**
   * Prevent body scroll when serene layout is active
   */
  useEffect(() => {
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, []);

  const variants = shouldReduceMotion ? reducedMotionVariants : containerVariants;

  return (
    <motion.div
      ref={containerRef}
      className={cn(
        // Full viewport coverage
        'fixed inset-0 z-50',
        // Flexbox centering for content
        'flex flex-col items-center justify-center',
        // Base background color (SereneBackground provides the visuals)
        'bg-[hsl(var(--serene-bg-primary))]',
        // Touch-friendly for mobile
        'touch-manipulation'
      )}
      role="main"
      aria-label="Serene breathing zone"
      variants={variants}
      initial="initial"
      animate="animate"
    >
      {/* Persistent SereneBackground - never unmounts during navigation */}
      <SereneBackground type={preferences.backgroundType} />

      {/* Top Right Controls - Theme Toggle and Exit Button */}
      <motion.div
        className={cn(
          'absolute right-4 top-4 z-20',
          'sm:right-6 sm:top-6',
          'md:right-8 md:top-8',
          'flex items-center gap-2'
        )}
        variants={shouldReduceMotion ? reducedMotionControlsVariants : controlsVariants}
        initial="initial"
        animate="animate"
      >
        {/* Settings Button */}
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setShowSettings(true)}
          className={cn(
            'rounded-full',
            'size-12 sm:size-11',
            'text-[hsl(190_18%_35%)] hover:text-[hsl(190_20%_28%)]',
            'dark:text-[hsl(175_12%_75%)] dark:hover:text-[hsl(175_15%_88%)]',
            'hover:bg-[hsl(175_22%_88%_/_0.5)]',
            'dark:hover:bg-[hsl(175_15%_25%_/_0.5)]',
            'focus-visible:ring-2 focus-visible:ring-[hsl(175_45%_55%)] focus-visible:ring-offset-2',
            'focus-visible:ring-offset-transparent',
            'transition-all duration-300'
          )}
          aria-label="Open settings"
        >
          <Settings className="size-6 sm:size-5" strokeWidth={1.5} />
        </Button>

        {/* Animated Theme Toggle */}
        <AnimatedThemeToggler
          duration={2400}
          animationOrigin="center"
          className={cn(
            'rounded-full',
            'size-12 sm:size-11',
            'inline-flex items-center justify-center',
            'text-[hsl(190_18%_35%)] hover:text-[hsl(190_20%_28%)]',
            'dark:text-[hsl(175_12%_75%)] dark:hover:text-[hsl(175_15%_88%)]',
            'bg-transparent hover:bg-[hsl(175_22%_88%_/_0.5)]',
            'dark:hover:bg-[hsl(175_15%_25%_/_0.5)]',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(175_45%_55%)]',
            'focus-visible:ring-offset-2 focus-visible:ring-offset-transparent',
            'transition-all duration-300'
          )}
        />

        {/* Exit Button with Tooltip */}
        <TooltipProvider delayDuration={300}>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                ref={exitButtonRef}
                variant="ghost"
                size="icon"
                onClick={handleExit}
                className={cn(
                  'rounded-full',
                  'size-12 sm:size-11',
                  'text-[hsl(190_18%_35%)] hover:text-[hsl(190_20%_28%)]',
                  'dark:text-[hsl(175_12%_75%)] dark:hover:text-[hsl(175_15%_88%)]',
                  'hover:bg-[hsl(175_22%_88%_/_0.5)]',
                  'dark:hover:bg-[hsl(175_15%_25%_/_0.5)]',
                  'focus-visible:ring-2 focus-visible:ring-[hsl(175_45%_55%)] focus-visible:ring-offset-2',
                  'focus-visible:ring-offset-transparent',
                  'transition-all duration-300'
                )}
                aria-label="Exit serene zone (Escape)"
                tabIndex={0}
              >
                <X className="size-6 sm:size-5" strokeWidth={1.5} />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="text-xs">
              {exitTooltipText}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </motion.div>

      {/* Audio Player - Top Left */}
      {preferences.audioEnabled && (
        <motion.div
          className="absolute left-4 top-4 z-20"
          variants={shouldReduceMotion ? reducedMotionControlsVariants : controlsVariants}
          initial="initial"
          animate="animate"
        >
          <SereneAudioPlayer showControls={true} />
        </motion.div>
      )}

      {/* Main Content Area - Child routes render here */}
      <div
        className={cn(
          'relative z-10 w-full',
          'flex flex-1 flex-col items-center justify-center',
          'px-4 py-8',
          'sm:px-6 sm:py-12',
          'md:px-12 md:py-20',
          'min-h-0 overflow-auto'
        )}
      >
        <Outlet />
      </div>

      {/* Quick Settings Panel */}
      <QuickSettingsPanel
        isOpen={showSettings}
        onClose={() => setShowSettings(false)}
        preferences={preferences}
        onPreferenceChange={updatePreference}
      />

      {/* Screen reader announcements */}
      <div className="sr-only" role="status" aria-live="polite">
        Entered serene breathing zone. Press Escape to exit.
      </div>
    </motion.div>
  );
}

// =============================================================================
// MAIN EXPORT
// =============================================================================

/**
 * SereneZoneLayout - Route-level layout for Serene Zone
 *
 * Wraps content with SereneProvider and renders persistent layout elements.
 * Use as an `element` in React Router route configuration.
 *
 * @example
 * ```tsx
 * // In router.tsx
 * {
 *   path: '/learning-hub/resource-library/tools/breathing',
 *   element: <SereneZoneLayout />,
 *   children: [
 *     { index: true, lazy: () => import('./routes/public/tools/breathing/index') },
 *     { path: '478', lazy: () => import('./routes/public/tools/breathing/four-seven-eight') },
 *     // ...
 *   ],
 * }
 * ```
 */
export function SereneZoneLayout() {
  return (
    <SereneProvider>
      <SereneZoneLayoutInner />
    </SereneProvider>
  );
}

SereneZoneLayout.displayName = 'SereneZoneLayout';
